import React, { useCallback, useEffect, useRef, useState } from "react";
import { useCopy } from "../../lib/paymentCheck/i18n.jsx";
import { track, FUNNEL_EVENTS } from "../../lib/paymentCheck/analytics.js";

const SLIDE_COUNT = 7;
const AUTOPLAY_INTERVAL_MS = 5500;
const RESUME_AFTER_MS = 6000;

/**
 * Full-bleed, high-contrast risk carousel — Art Direction V2, mobile
 * bug fixed in V2.1, affordance refined in V2.2. "NEXT ITERATION"
 * (task brief §11-14) expands this to 7 slides sharing one visual
 * grammar (same yellow-flag/kicker treatment, dimensions, typography
 * hierarchy) and adds restrained autoplay.
 *
 * Slide 1 is an explicitly labelled illustrative example (not a real
 * case). Slides 2-6 are sourced statistics (FBI IC3, UK Finance,
 * Verizon DBIR — see copy.js header for how each figure was checked)
 * translated into a personal "why this matters to you" line, each
 * carrying a "Sourced statistic" flag distinct from slide 1's
 * "Illustrative example" flag so the two are never visually confused.
 * Slide 6 is the crypto-specific slide with a short narrative. Slide 7
 * is the closing/CTA slide.
 *
 * Autoplay: a slow (5.5s), restrained loop, **desktop-only** (≥1024px) —
 * paused immediately by any swipe, arrow click, hover or keyboard focus,
 * and resumed only after a period of inactivity. Entirely disabled under
 * `prefers-reduced-motion: reduce`.
 *
 * MOBILE QA FIX (real-iPhone report): autoplay was calling
 * `card.scrollIntoView({ block: "nearest" })` to advance slides. Even
 * with `block: "nearest"`, `scrollIntoView` walks every scrollable
 * ancestor up to and including the document to bring the target into
 * view — not just the carousel's own horizontal scroller. On a real
 * phone, once the visitor scrolled back up the page away from the
 * carousel, the next autoplay tick would scroll the whole PAGE back
 * down to the carousel to satisfy `scrollIntoView`, making normal
 * scrolling impossible. Fixed by never calling `scrollIntoView` for
 * carousel navigation at all — every programmatic slide change now
 * calls `scrollTo`/sets `scrollLeft` directly on the carousel's own
 * track element (`trackRef`), which only ever affects that element's
 * own internal horizontal scroll position and cannot move the page or
 * browser focus, regardless of what triggered it. Autoplay is also now
 * gated to desktop widths only (≥1024px, the breakpoint where this
 * carousel already switches to a multi-up gallery and mouse/keyboard
 * become the primary interaction) as an extra safeguard, per the
 * Product Owner's explicit instruction to disable it on mobile
 * entirely for now.
 *
 * Active-slide detection derives from `scrollLeft / slideWidth` rather
 * than comparing every slide's `getBoundingClientRect()`, which is
 * simpler and doesn't depend on layout timing.
 */
export default function RiskCarousel({ onGetItChecked, ctaLabel }) {
  const t = useCopy();
  const trackRef = useRef(null);
  const [active, setActive] = useState(0);
  const [reducedMotion, setReducedMotion] = useState(false);
  const [isDesktop, setIsDesktop] = useState(false);
  const [autoplayPaused, setAutoplayPaused] = useState(false);
  const resumeTimerRef = useRef(0);
  const autoplayTimerRef = useRef(0);
  const activeRef = useRef(0);

  useEffect(() => {
    activeRef.current = active;
  }, [active]);

  useEffect(() => {
    const mq = window.matchMedia?.("(prefers-reduced-motion: reduce)");
    if (!mq) return;
    setReducedMotion(mq.matches);
    const onChange = (e) => setReducedMotion(e.matches);
    mq.addEventListener?.("change", onChange);
    return () => mq.removeEventListener?.("change", onChange);
  }, []);

  // Autoplay is desktop-only (task brief: "disable carousel autoplay
  // completely [on mobile] for now"). 1024px matches the breakpoint
  // where this carousel already becomes a multi-up gallery.
  useEffect(() => {
    const mq = window.matchMedia?.("(min-width: 1024px)");
    if (!mq) return;
    setIsDesktop(mq.matches);
    const onChange = (e) => setIsDesktop(e.matches);
    mq.addEventListener?.("change", onChange);
    return () => mq.removeEventListener?.("change", onChange);
  }, []);

  useEffect(() => {
    const el = trackRef.current;
    if (!el) return;
    let raf = 0;
    const onScroll = () => {
      if (raf) return;
      raf = requestAnimationFrame(() => {
        raf = 0;
        const first = el.querySelector("[data-carousel-card]");
        const slideWidth = first?.getBoundingClientRect().width;
        if (!slideWidth) return;
        const index = Math.round(el.scrollLeft / slideWidth);
        setActive(Math.min(Math.max(index, 0), SLIDE_COUNT - 1));
      });
    };
    el.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      el.removeEventListener("scroll", onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, []);

  // Deliberately does NOT use `scrollIntoView` (see the mobile-QA-fix
  // note in the file header) — `el.scrollTo` only ever touches this
  // element's OWN scroll position, never any ancestor's, so it can
  // never move the page or browser focus, no matter what triggered it.
  const scrollToIndex = useCallback(
    (index, { smooth = true } = {}) => {
      const el = trackRef.current;
      if (!el) return;
      const first = el.querySelector("[data-carousel-card]");
      const slideWidth = first?.getBoundingClientRect().width;
      if (!slideWidth) return;
      el.scrollTo({
        left: index * slideWidth,
        behavior: smooth && !reducedMotion ? "smooth" : "auto",
      });
    },
    [reducedMotion]
  );

  const scrollByCard = (dir) => scrollToIndex(Math.min(Math.max(active + dir, 0), SLIDE_COUNT - 1));

  // Autoplay: a single interval that advances one slide, looping back
  // to the start after the last one. Any interaction pauses it
  // immediately via `pauseAutoplay`; it resumes on its own after
  // `RESUME_AFTER_MS` of no further interaction.
  useEffect(() => {
    if (reducedMotion || autoplayPaused || !isDesktop) return undefined;
    autoplayTimerRef.current = window.setInterval(() => {
      const next = activeRef.current >= SLIDE_COUNT - 1 ? 0 : activeRef.current + 1;
      scrollToIndex(next);
    }, AUTOPLAY_INTERVAL_MS);
    return () => window.clearInterval(autoplayTimerRef.current);
  }, [reducedMotion, autoplayPaused, isDesktop, scrollToIndex]);

  const pauseAutoplay = useCallback(() => {
    if (reducedMotion) return;
    setAutoplayPaused(true);
    window.clearTimeout(resumeTimerRef.current);
    resumeTimerRef.current = window.setTimeout(() => setAutoplayPaused(false), RESUME_AFTER_MS);
  }, [reducedMotion]);

  useEffect(() => () => {
    window.clearTimeout(resumeTimerRef.current);
    window.clearInterval(autoplayTimerRef.current);
  }, []);

  const onTrackKeyDown = (e) => {
    pauseAutoplay();
    if (e.key === "ArrowRight") {
      e.preventDefault();
      scrollByCard(1);
    } else if (e.key === "ArrowLeft") {
      e.preventDefault();
      scrollByCard(-1);
    }
  };

  return (
    <div
      className="pc-carousel-wrap"
      onMouseEnter={pauseAutoplay}
      onTouchStart={pauseAutoplay}
      onFocus={pauseAutoplay}
    >
      <div className="pc-carousel__hintrow">
        <p className="pc-carousel__hint" aria-hidden="true">
          {t("carousel_swipe_hint")}
        </p>
        {autoplayPaused && !reducedMotion && isDesktop && (
          <p className="pc-carousel__pausedhint" aria-hidden="true">
            {t("carousel_paused_hint")}
          </p>
        )}
      </div>
      <div
        className="pc-carousel"
        ref={trackRef}
        role="region"
        aria-roledescription="carousel"
        aria-label={t("carousel_eyebrow")}
        onKeyDown={onTrackKeyDown}
        onPointerDown={pauseAutoplay}
      >
        <article className="pc-slide" data-carousel-card tabIndex={0} role="group" aria-roledescription="slide" aria-label={`1 of ${SLIDE_COUNT}`}>
          <span className="pc-slide__flag">{t("carousel_illustrative_flag")}</span>
          <p className="pc-slide__figure">{t("carousel_card1_amount")}</p>
          <p className="pc-slide__line">{t("carousel_card1_line1")}</p>
          <p className="pc-slide__line">{t("carousel_card1_line2")}</p>
          <p className="pc-slide__line">{t("carousel_card1_line3")}</p>
        </article>

        <article className="pc-slide" data-carousel-card tabIndex={0} role="group" aria-roledescription="slide" aria-label={`2 of ${SLIDE_COUNT}`}>
          <span className="pc-slide__flag pc-slide__flag--sourced">{t("carousel_sourced_flag")}</span>
          <span className="pc-slide__kicker">{t("carousel_card2_kicker")}</span>
          <p className="pc-slide__figure">{t("carousel_card2_stat")}</p>
          <p className="pc-slide__caption">{t("carousel_card2_caption")}</p>
          <a className="pc-slide__source" href={t("carousel_card2_source_url")} target="_blank" rel="noreferrer">{t("carousel_card2_source")}</a>
        </article>

        <article className="pc-slide" data-carousel-card tabIndex={0} role="group" aria-roledescription="slide" aria-label={`3 of ${SLIDE_COUNT}`}>
          <span className="pc-slide__flag pc-slide__flag--sourced">{t("carousel_sourced_flag")}</span>
          <span className="pc-slide__kicker">{t("carousel_card3_kicker")}</span>
          <p className="pc-slide__figure">{t("carousel_card3_stat")}</p>
          <p className="pc-slide__caption">{t("carousel_card3_caption")}</p>
          <a className="pc-slide__source" href={t("carousel_card3_source_url")} target="_blank" rel="noreferrer">{t("carousel_card3_source")}</a>
        </article>

        <article className="pc-slide" data-carousel-card tabIndex={0} role="group" aria-roledescription="slide" aria-label={`4 of ${SLIDE_COUNT}`}>
          <span className="pc-slide__flag pc-slide__flag--sourced">{t("carousel_sourced_flag")}</span>
          <span className="pc-slide__kicker">{t("carousel_card4_kicker")}</span>
          <p className="pc-slide__figure">{t("carousel_card4_stat")}</p>
          <p className="pc-slide__caption">{t("carousel_card4_caption")}</p>
          <a className="pc-slide__source" href={t("carousel_card4_source_url")} target="_blank" rel="noreferrer">{t("carousel_card4_source")}</a>
        </article>

        <article className="pc-slide" data-carousel-card tabIndex={0} role="group" aria-roledescription="slide" aria-label={`5 of ${SLIDE_COUNT}`}>
          <span className="pc-slide__flag pc-slide__flag--sourced">{t("carousel_sourced_flag")}</span>
          <span className="pc-slide__kicker">{t("carousel_card5_kicker")}</span>
          <p className="pc-slide__figure">{t("carousel_card5_stat")}</p>
          <p className="pc-slide__caption">{t("carousel_card5_caption")}</p>
          <a className="pc-slide__source" href={t("carousel_card5_source_url")} target="_blank" rel="noreferrer">{t("carousel_card5_source")}</a>
        </article>

        <article className="pc-slide" data-carousel-card tabIndex={0} role="group" aria-roledescription="slide" aria-label={`6 of ${SLIDE_COUNT}`}>
          <span className="pc-slide__flag pc-slide__flag--sourced">{t("carousel_sourced_flag")}</span>
          <span className="pc-slide__kicker">{t("carousel_card6_kicker")}</span>
          <p className="pc-slide__figure">{t("carousel_card6_stat")}</p>
          <p className="pc-slide__line pc-slide__line--accent">{t("carousel_card6_line1")}</p>
          <p className="pc-slide__line pc-slide__line--accent">{t("carousel_card6_line2")}</p>
          <p className="pc-slide__caption">{t("carousel_card6_caption")}</p>
          <a className="pc-slide__source" href={t("carousel_card6_source_url")} target="_blank" rel="noreferrer">{t("carousel_card6_source")}</a>
        </article>

        <article className="pc-slide pc-slide--cta" data-carousel-card tabIndex={0} role="group" aria-roledescription="slide" aria-label={`7 of ${SLIDE_COUNT}`}>
          <p className="pc-slide__question">{t("carousel_card7_title")}</p>
          <p className="pc-slide__line pc-slide__line--lg">{t("carousel_card7_body")}</p>
          <p className="pc-slide__line pc-slide__line--lg pc-slide__line--accent">{t("carousel_card7_body_2")}</p>
          <p className="pc-slide__closing">
            {t("carousel_card7_closing1")} {t("carousel_card7_closing2")}
          </p>
          <button
            type="button"
            className="pc-btn pc-btn--yellow"
            onClick={() => {
              track(FUNNEL_EVENTS.CTA_CLICK, { surface: "carousel" });
              onGetItChecked();
            }}
          >
            {ctaLabel || t("carousel_card7_cta")} <span aria-hidden="true">→</span>
          </button>
        </article>
      </div>

      <div className="pc-carousel__controls">
        <button
          type="button"
          className="pc-carousel__navbtn"
          aria-label={t("carousel_prev")}
          onClick={() => {
            pauseAutoplay();
            scrollByCard(-1);
          }}
          disabled={active === 0}
        >
          ←
        </button>
        <div className="pc-carousel__dots" role="tablist" aria-label="Slide">
          {Array.from({ length: SLIDE_COUNT }).map((_, i) => (
            <button
              key={i}
              type="button"
              role="tab"
              aria-selected={i === active}
              aria-label={`Slide ${i + 1} of ${SLIDE_COUNT}`}
              className="pc-carousel__dot"
              data-active={i === active}
              onClick={() => {
                pauseAutoplay();
                scrollToIndex(i);
              }}
            />
          ))}
        </div>
        <button
          type="button"
          className="pc-carousel__navbtn"
          aria-label={t("carousel_next")}
          onClick={() => {
            pauseAutoplay();
            scrollByCard(1);
          }}
          disabled={active === SLIDE_COUNT - 1}
        >
          →
        </button>
      </div>
      <p className="pc-carousel__count" aria-live="polite">
        {active + 1} {t("carousel_slide_count")} {SLIDE_COUNT}
      </p>
    </div>
  );
}
